<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  client: { type: Object, default: null },
});

const { t } = useI18n();

const SERVICE_STATUS_CLASSES = {
  Active: 'bg-n-teal-3 text-n-teal-11',
  Suspended: 'bg-n-amber-3 text-n-amber-11',
  Pending: 'bg-n-amber-3 text-n-amber-11',
};

const service = computed(() => props.ticket.service);

const serviceStatus = computed(() => {
  const status = service.value?.status;
  if (!status) return null;
  const key = status.toUpperCase().replace(/[^A-Z]/g, '_');
  return {
    label: t(`WHMCS_PANEL.SERVICE_STATUS.${key}`, status),
    classes: SERVICE_STATUS_CLASSES[status] || 'bg-n-alpha-2 text-n-slate-11',
  };
});

const billing = computed(() => {
  if (!service.value) return '';
  const {
    amount,
    billing_cycle: cycle,
    next_due_date: dueDate,
  } = service.value;
  const parts = [];
  if (Number(amount) > 0) {
    parts.push(
      Number(amount).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    );
  }
  if (cycle) parts.push(t(`WHMCS_PANEL.BILLING_CYCLE.${cycle}`, cycle));
  if (dueDate && !dueDate.startsWith('0000')) {
    const [year, month, day] = dueDate.split('-');
    parts.push(t('WHMCS_PANEL.DUE_ON', { date: `${day}/${month}/${year}` }));
  }
  return parts.join(' · ');
});

const lastLogin = computed(() => {
  if (!props.client?.last_login_ip) return '';
  return [props.client.last_login_at, props.client.last_login_ip]
    .filter(Boolean)
    .join(' · ');
});
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3 border-b border-n-weak">
    <div>
      <div class="mb-1 text-xs text-n-slate-10">
        {{ t('WHMCS_PANEL.TICKET') }}
      </div>
      <a
        :href="ticket.url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-slate-12 hover:text-n-brand"
      >
        {{ `${ticket.tid} · ${ticket.department}` }}
        <Icon icon="i-lucide-external-link" class="size-3.5" />
      </a>
    </div>
    <div v-if="service">
      <div class="mb-1 text-xs text-n-slate-10">
        {{ t('WHMCS_PANEL.SERVICE') }}
      </div>
      <div class="flex items-start justify-between gap-2">
        <a
          :href="service.url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm font-medium text-n-slate-12 hover:text-n-brand"
        >
          {{ service.name }}
          <span class="block text-xs font-normal text-n-slate-11">
            {{ `${service.group} · #${service.id}` }}
          </span>
        </a>
        <span
          v-if="serviceStatus"
          class="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded-md"
          :class="serviceStatus.classes"
        >
          {{ serviceStatus.label }}
        </span>
      </div>
      <div v-if="billing" class="mt-1 text-xs text-n-slate-11">
        {{ billing }}
      </div>
      <div v-if="service.ip" class="mt-1 text-xs text-n-slate-11">
        {{ t('WHMCS_PANEL.SERVICE_IP', { ip: service.ip }) }}
      </div>
    </div>
    <div v-else class="text-xs text-n-slate-10">
      {{ t('WHMCS_PANEL.NO_SERVICE') }}
    </div>
    <div v-if="client">
      <div class="mb-1 text-xs text-n-slate-10">
        {{ t('WHMCS_PANEL.CLIENT') }}
      </div>
      <a
        :href="client.url"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-n-slate-12 hover:text-n-brand"
      >
        {{
          client.location
            ? `#${client.client_id} · ${client.location}`
            : `#${client.client_id}`
        }}
        <Icon icon="i-lucide-external-link" class="size-3.5" />
      </a>
      <div v-if="lastLogin" class="mt-1 text-xs text-n-slate-11">
        {{ t('WHMCS_PANEL.LAST_LOGIN', { login: lastLogin }) }}
        <span v-if="client.last_login_host" class="block truncate">
          {{ client.last_login_host }}
        </span>
      </div>
    </div>
  </div>
</template>
